import {
  AccountCircle as AccountCircleIcon,
  Agriculture as AgricultureIcon,
  Cloud as CloudIcon,
  Dashboard as DashboardIcon,
  Home as HomeIcon,
  LocalOffer as LocalOfferIcon,
  MenuBook as MenuBookIcon,
  Message as MessageIcon,
  Nature as NatureIcon,
  Person as PersonIcon,
  Sensors as SensorsIcon,
  Settings as SettingsIcon,
  Warning as WarningIcon,
  Water as WaterIcon,
  Work as WorkIcon,
} from '@mui/icons-material/';
import { UserRole, UserType } from '../../../constants/user_types';
import { NodeSchema, SectorSchema, SensorModelSchema } from '../../../requests/api/apiTypes';

export interface SensorTypeStatus {
  suelo: boolean;
  clima: boolean;
  riego: boolean;
  planta: boolean;
}

interface NavItem {
  title: string;
  icon?: any;
  href?: string;
  items?: NavItem[];
  sensorTypes?: SensorTypeStatus;
  userTypesAllowed: UserType[];
  userRolesAllowed?: UserRole[];
}

export const navConfig: NavItem[] = [
  {
    title: 'Usuarios',
    icon: PersonIcon,
    href: '/usuarios',
    userTypesAllowed: [UserType.CDTEC],
    userRolesAllowed: [UserRole.ADMIN, UserRole.OPERATOR],
  },
  {
    title: 'Cuentas',
    icon: AccountCircleIcon,
    href: '/cuentas',
    userTypesAllowed: [UserType.CDTEC],
  },
  {
    title: 'Empresas',
    icon: WorkIcon,
    href: '/empresas',
    userTypesAllowed: [UserType.CDTEC],
    userRolesAllowed: [UserRole.ADMIN, UserRole.OPERATOR],
  },
  {
    title: 'Equipos',
    icon: SensorsIcon,
    href: '/equipos',
    userTypesAllowed: [UserType.CDTEC],
    userRolesAllowed: [UserRole.ADMIN],
  },
  {
    title: 'Campos',
    icon: AgricultureIcon,
    href: '/campos',
    userTypesAllowed: [UserType.CDTEC],
    userRolesAllowed: [UserRole.ADMIN],
  },
  {
    title: 'Especies y variedades',
    icon: NatureIcon,
    href: '/especies-variedades',
    userTypesAllowed: [UserType.CDTEC],
    userRolesAllowed: [UserRole.ADMIN, UserRole.OPERATOR, UserRole.AGRO],
  },
  {
    title: 'Documentación',
    icon: MenuBookIcon,
    href: '/documentacion',
    userTypesAllowed: [UserType.CDTEC],
    userRolesAllowed: [UserRole.ADMIN],
  },
];

const specificNavConfig: NavItem[] = [
  {
    title: 'Inicio',
    icon: HomeIcon,
    href: '/inicio',
    userTypesAllowed: [
      UserType.CDTEC, UserType.CONSULTANT, UserType.CLIENT, UserType.MULTI_ACCOUNT_CLIENT],
  },
  {
    title: 'Sectores',
    icon: DashboardIcon,
    items: [],
    userTypesAllowed: [
      UserType.CDTEC, UserType.CONSULTANT, UserType.CLIENT, UserType.MULTI_ACCOUNT_CLIENT],
  },
  {
    title: 'Estaciones de clima',
    icon: CloudIcon,
    items: [],
    userTypesAllowed: [
      UserType.CDTEC, UserType.CONSULTANT, UserType.CLIENT, UserType.MULTI_ACCOUNT_CLIENT],
  },
  {
    title: 'Monitoreo equipos',
    icon: SensorsIcon,
    href: '/monitoreo-equipos',
    userTypesAllowed: [
      UserType.CDTEC, UserType.CONSULTANT, UserType.CLIENT, UserType.MULTI_ACCOUNT_CLIENT],
  },
  {
    title: 'Configuración Campo',
    icon: SettingsIcon,
    href: '/layout',
    userTypesAllowed: [UserType.CDTEC],
    userRolesAllowed: [UserRole.ADMIN, UserRole.OPERATOR],
  },
  {
    title: 'Riego',
    icon: WaterIcon,
    href: '/riego',
    userTypesAllowed: [
      UserType.CDTEC, UserType.CONSULTANT, UserType.CLIENT, UserType.MULTI_ACCOUNT_CLIENT],
  },
  {
    title: 'Conversaciones',
    icon: MessageIcon,
    href: '/conversaciones',
    userTypesAllowed: [
      UserType.CDTEC, UserType.CONSULTANT, UserType.CLIENT, UserType.MULTI_ACCOUNT_CLIENT],
  },
  {
    title: 'Alertas',
    icon: WarningIcon,
    href: '/alertas',
    userTypesAllowed: [
      UserType.CDTEC, UserType.CONSULTANT, UserType.CLIENT, UserType.MULTI_ACCOUNT_CLIENT],
  },
  {
    title: 'Órdenes de trabajo',
    icon: LocalOfferIcon,
    href: '/ordenes-de-trabajo',
    userTypesAllowed: [UserType.CDTEC],
    userRolesAllowed: [UserRole.ADMIN, UserRole.OPERATOR],
  },
  {
    title: 'Usuarios',
    icon: PersonIcon,
    href: '/cliente-usuarios',
    userTypesAllowed: [
      UserType.CDTEC, UserType.CONSULTANT, UserType.CLIENT, UserType.MULTI_ACCOUNT_CLIENT],
  },
];

export const sensorTypes = ['suelo', 'clima', 'planta', 'riego'];

export const getSectorsBySensorType = (
  sectors: Array<any>,
  devices: Array<SensorModelSchema>,
  sensorType: string,
): Array<any> => sectors?.filter((sector) => {
  const sectorDevices = devices.filter((device) => device.sector?.id === sector.id
        && device.sensorType === sensorType);
  return sectorDevices.length > 0;
}) || [];

const getSectorWithDevices = (sector: any, sensors: Array<NodeSchema | SensorModelSchema>) => {
  const sectorDevices = sensors.filter((sensor) => sensor?.sector?.id === sector.id && !('nodeType' in sensor));
  return {
    suelo: sectorDevices.some(
      (sensor) => (sensor as SensorModelSchema)?.sensorType === 'Suelo',
    ),
    clima: sectorDevices.some(
      (sensor) => (sensor as SensorModelSchema)?.sensorType === 'Clima',
    ),
    riego: sectorDevices.some(
      (sensor) => (sensor as SensorModelSchema)?.sensorType === 'Riego',
    ),
    planta: sectorDevices.some(
      (sensor) => (sensor as SensorModelSchema)?.sensorType === 'Planta',
    ),
  };
};

// Definimos un mapa de emojis para cada tipo de sensor
const SENSOR_TYPE_EMOJI = {
  suelo: '🌱',
  clima: '🌤️',
  planta: '🪴',
  riego: '💧',
} as const;

export const filterNavItemsByRole = (
  items: NavItem[],
  userType: UserType,
  userRole?: UserRole,
): NavItem[] => items.filter((item) => {
  const isUserTypeAllowed = item.userTypesAllowed.includes(userType);

  const isUserRoleAllowed = !item.userRolesAllowed
    || (userRole && item.userRolesAllowed.includes(userRole));

  return isUserTypeAllowed && isUserRoleAllowed;
});

export const getUpdatedSpecificNavConfig = (
  sectors: Array<SectorSchema> = [],
  devices: Array<NodeSchema | SensorModelSchema> = [],
  userType?: UserType,
  userRole?: UserRole,
  devicesLoaded = true,
  farmId?: string,
) => {
  const filteredConfig = filterNavItemsByRole(specificNavConfig, userType!, userRole);

  const sectoresIndex = filteredConfig.findIndex((item) => item.title === 'Sectores');
  if (sectoresIndex !== -1) {
    filteredConfig[sectoresIndex].items = sectors.map((sector) => {
      const sensorTypesNames = getSectorWithDevices(sector, devices);
      const sensorEmojis = Object.entries(sensorTypesNames)
        .filter(([, hasType]) => hasType)
        .map(([type]) => SENSOR_TYPE_EMOJI[type as keyof typeof SENSOR_TYPE_EMOJI])
        .join(' ');
      return {
        title: `${sector.name} ${sensorEmojis}`,
        href: farmId
          ? `/sectores/${sector.id}?source=inicio&farmId=${farmId}`
          : `/sectores/${sector.id}?source=inicio`,
        sensorTypes: sensorTypesNames,
        userTypesAllowed: [
          UserType.CDTEC, UserType.CONSULTANT, UserType.CLIENT, UserType.MULTI_ACCOUNT_CLIENT],
      } as NavItem;
    });
  }

  const weatherIndex = filteredConfig
    .findIndex((item) => item.title === 'Estaciones de clima');
  if (weatherIndex !== -1) {
    const weatherStations = (devices as SensorModelSchema[])
      .filter((d) => 'isWeatherStation' in d && d.isWeatherStation);
    if (weatherStations.length > 0) {
      filteredConfig[weatherIndex].items = weatherStations
        .map((station) => ({
          title: station.name,
          href: `/weather-station/${station.id}`,
          userTypesAllowed: [
            UserType.CDTEC, UserType.CONSULTANT, UserType.CLIENT,
            UserType.MULTI_ACCOUNT_CLIENT],
        } as NavItem));
    } else if (devicesLoaded) {
      filteredConfig[weatherIndex].href = '/weather-station';
      filteredConfig[weatherIndex].items = [];
    }
  }

  return filteredConfig;
};
